import React, { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import useThemeColor from "../../hooks/useThemeColor";
import {
  Panel,
  PanelBody,
  PanelHeader,
} from "../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ModalButtons from "../../components/CustomComponents/Buttons/Close&SaveButton/Save&CloseButton.jsx";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel.jsx";
import {
  checkIfCommonTimeSlots,
  convertToScheduleFormat,
} from "../../components/CustomComponents/TimeSchedule/scheduleConv.js";
import { appSettings } from "../../app/appSettingsSlice";

function UpdateMenu() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Theme-aware background and text colors
  const themeBg = isDarkMode ? '#2c3034' : '#f8f9fa';
  const themeBorder = isDarkMode ? '#495057' : '#dee2e6';
  const themeText = isDarkMode ? '#e9ecef' : '#495057';
  const themeLightBg = isDarkMode ? '#495057' : '#e9ecef';

  const toggleLabelStyle = {
    color: themeText,
  };

  const [menuName, setMenuName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [originalMenuName, setOriginalMenuName] = useState("");
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [enableMenu, setEnableMenu] = useState(false);
  const [regularHours, setRegularHours] = useState(true);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [epos, setEpos] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const [online, setOnline] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [menuGroups, setMenuGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [menuOrderby, setMenuOrderBy] = useState(0);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    { from: "00:00", to: "23:59" },
  ]);
  const [schedules, setSchedules] = useState(schedule);
  const [savedData, setSavedData] = useState({
    commonTimeSlot: [{ from: "00:00", to: "23:59" }],
    schedules: schedule,
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Fetch menu details and products (unchanged)
  const fetchMenuDetails = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axiosInstance.get(APIs.GET_MENU_BY_MENU_ID, {
        params: { CompID, MenuID: menuId },
      });
      console.log(response.data.data);
      // Menu Details Response logged

      const menuDetails = response.data.data || response.data;
      // Menu Details logged
   
      setMenuGroups(
        (menuDetails.MenuGroups || menuDetails.menuGroups || []).map((item, idx) => ({
          menuGroupID: item.menuGroupID || item.MenuGroupID || 0,
          Menu_ID: parseInt(item.Menu_ID || item.menu_ID || menuId),
          localizeID: item.localizeID || item.LocalizeID || "en",
          compID: item.compID || item.CompID || CompID,
          menuGroup_Name: item.menuGroup_Name || item.MenuGroup_Name || "",
          menuGroup_Publish: item.menuGroup_Publish || item.MenuGroup_Publish !== false,
          menuGroup_Orderby: item.menuGroup_Orderby || item.MenuGroup_Orderby || idx,
          updateDateTime: item.updateDateTime || item.UpdateDateTime || new Date().toISOString(),
          expanded: false,
          selectedProduct: item.products || [],
        }))
      );

      setEpos(menuDetails.IsActiveforTill || menuDetails.isActiveforTill);
      setOnline(menuDetails.IsActiveforTakeAway || menuDetails.isActiveforTakeAway);
      setKiosk(menuDetails.IsActiveforKIOSK || menuDetails.isActiveforKIOSK);
      setDelivery(menuDetails.IsActiveforDelivery || menuDetails.isActiveforDelivery);
      setMenuName(menuDetails.Menu_Name || menuDetails.menu_Name || "");
      setEnableMenu(menuDetails.Menu_Publish !== false && menuDetails.menu_Publish !== false);
      setRegularHours(!(menuDetails.IsAvailableInTimeIntervals || menuDetails.isAvailableInTimeIntervals));
      setMenuOrderBy(menuDetails.Menu_Orderby || menuDetails.menu_Orderby || 0);

      if (
        menuDetails.menuWeeklyTimings &&
        Array.isArray(menuDetails.menuWeeklyTimings)
      ) {
        const isCommon = checkIfCommonTimeSlots(menuDetails.menuWeeklyTimings);
        if (isCommon) {
          const firstDaySlots = menuDetails.menuWeeklyTimings
            .filter(
              (day) => day.dayName === menuDetails.menuWeeklyTimings[0].dayName
            )
            .map((day) => {
              // Convert times from backend format (could be 24-hour or 12-hour) to 12-hour with AM/PM
              let fromTime = moment(day.openTime, ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
              let toTime = moment(day.closeTime, ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
              
              // If parsing failed, try without strict mode as fallback
              if (!fromTime.isValid() && day.openTime) {
                fromTime = moment(day.openTime.trim(), ["h:mm A", "hh:mm A", "H:mm", "HH:mm"]);
              }
              if (!toTime.isValid() && day.closeTime) {
                toTime = moment(day.closeTime.trim(), ["h:mm A", "hh:mm A", "H:mm", "HH:mm"]);
              }
              
              return {
                from: fromTime.isValid() ? fromTime.format("h:mm A") : "",
                to: toTime.isValid() ? toTime.format("h:mm A") : "",
              };
            })
            .filter(slot => slot.from && slot.to); // Only include slots with valid times

          setCommonTimeSlot(firstDaySlots);
        } else {
          setUseSameTimeSlots(false);

          const convertedSchedules = convertToScheduleFormat(menuDetails.menuWeeklyTimings);
          setSchedules(convertedSchedules);
        }
      }
    } catch (err) {
      // Error fetching menu details silently
    } finally {
      setLoading(false);
    }
  }, [CompID, menuId]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_SHORTED_PRODUCTS, {
        params: { CompID },
      });
      // Response data logged

      const productsData = response.data.data || response.data;
      if (Array.isArray(productsData)) {
        setProducts(productsData);
      } else {
        // Unexpected API response format silently
        setProducts([]);
      }
    } catch (error) {
      // Error logged
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    fetchMenuDetails();
    fetchProducts();
  }, [fetchMenuDetails, fetchProducts]);

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // Allow dropping
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newMenuGroups = [...menuGroups];
    const [draggedItem] = newMenuGroups.splice(draggedIndex, 1); // Remove dragged item
    newMenuGroups.splice(dropIndex, 0, draggedItem); // Insert at new position

    // Update menuGroup_Orderby for all items
    const updatedMenuGroups = newMenuGroups.map((group, idx) => ({
      ...group,
      menuGroup_Orderby: idx,
    }));

    setMenuGroups(updatedMenuGroups);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null); // Reset dragged index
  };

  const handleMenuGroupChange = (index, value) => {
    setMenuGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index ? { ...group, menuGroup_Name: value } : group
      )
    );
  };

  const handleAddMenuGroup = () => {
    setMenuGroups([
      ...menuGroups,
      {
        menuGroupID: 0,
        Menu_ID: parseInt(menuId),
        localizeID: "en",
        compID: CompID,
        menuGroup_Name: "",
        menuGroup_Publish: true,
        menuGroup_Orderby: menuGroups.length, // Set order as last item
        updateDateTime: new Date().toISOString(),
        expanded: false,
        selectedProduct: [],
      },
    ]);
  };

  const handleDeleteMenuGroup = (index) => {
    const newMenuGroups = menuGroups
      .filter((_, i) => i !== index)
      .map((group, idx) => ({
        ...group,
        menuGroup_Orderby: idx, // Reorder after deletion
      }));
    setMenuGroups(newMenuGroups); // Update order after deletion
  };

  const toggleGroupExpansion = (index) => {
    setMenuGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index ? { ...group, expanded: !group.expanded } : group
      )
    );
  };

  const handleAddProduct = (groupIndex) => {
    setCurrentGroupIndex(groupIndex);
    setSelectedProducts(menuGroups[groupIndex].selectedProduct || []);
    setShowModal(true);
  };

  const handleProductSelectionSubmit = (selectedProducts) => {
    if (currentGroupIndex !== null) {
      setMenuGroups((prevGroups) =>
        prevGroups.map((group, index) =>
          index === currentGroupIndex
            ? { ...group, selectedProduct: selectedProducts }
            : group
        )
      );
    }
    setShowModal(false);
    setCurrentGroupIndex(null);
  };

  const applyCommonTimeSlotToAll = () => {
    // Ensure commonTimeSlot is treated as an array
    const slotArray = Array.isArray(commonTimeSlot) ? commonTimeSlot : [];

    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: slotArray.map((slot) => ({ ...slot })),
    }));

    setSchedules(updatedSchedules);
    setSavedData((prev) => ({ ...prev, schedules: updatedSchedules }));
  };

  const handleRegularHoursToggle = () => {
    const newRegularHours = !regularHours;
    setRegularHours(newRegularHours);
    if (!newRegularHours) {
      setSchedules(savedData.schedules);
      setCommonTimeSlot(savedData.commonTimeSlot);
    } else {
      setSavedData({
        commonTimeSlot: [...commonTimeSlot],
        schedules: [...schedules],
      });
    }
  };

  const handleSameTimeSlotsToggle = () => {
    const newUseSameTimeSlots = !useSameTimeSlots;
    setUseSameTimeSlots(newUseSameTimeSlots);
    if (newUseSameTimeSlots) {
      setSavedData((prev) => ({ ...prev, schedules: [...schedules] }));
      applyCommonTimeSlotToAll();
    } else {
      if (savedData.schedules.some((day) => day.timeSlots.length > 0)) {
        setSchedules(savedData.schedules);
      }
    }
  };

  const handleUpdate = async () => {
    if (!menuName.trim()) {
      showErrorToast("Menu name is required");
      return;
    }

    // Check if at least one menu group has a name
    const validGroups = menuGroups.filter((group) => group.menuGroup_Name?.trim().length > 0);
    if (validGroups.length === 0) {
      showErrorToast("Please add at least one menu group with a name.");
      return;
    }

    setLoading(true);

    const formattedSchedules = [];

    if (!regularHours) {
      if (useSameTimeSlots) {
        const days = [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ];
        days.forEach((day) => {
          // If there are time slots, add them; otherwise, explicitly set an empty array
          if (commonTimeSlot.length > 0) {
            commonTimeSlot.forEach((slot) => {
              // Parse time with multiple format options to handle AM/PM correctly
              const fromTime = moment(slot.from.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
              const toTime = moment(slot.to.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
              
              if (fromTime.isValid() && toTime.isValid()) {
                formattedSchedules.push({
                  ID: 0,
                  CompID: CompID,
                  Menu_ID: parseInt(menuId),
                  DayName: day,
                  OpenTime: fromTime.format("hh:mm A"),
                  CloseTime: toTime.format("hh:mm A"),
                  UpdateDateTime: new Date().toISOString(),
                });
              }
            });
          }
        });
      } else {
        // Process each day of the schedule separately
        schedules.forEach((schedule) => {
          if (schedule.checked && schedule.timeSlots.length > 0) {
            // Process checked days with time slots
            schedule.timeSlots.forEach((slot) => {
              // Parse time with multiple format options to handle AM/PM correctly
              const fromTime = moment(slot.from.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
              const toTime = moment(slot.to.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm", "HH:mm"], true);
              
              if (fromTime.isValid() && toTime.isValid()) {
                formattedSchedules.push({
                  ID: 0,
                  CompID: CompID,
                  Menu_ID: parseInt(menuId),
                  DayName: schedule.day,
                  OpenTime: fromTime.format("hh:mm A"),
                  CloseTime: toTime.format("hh:mm A"),
                  UpdateDateTime: new Date().toISOString(),
                });
              }
            });
          }
        });
      }
    }

    const payload = {
      Menu_ID: parseInt(menuId),
      CompID: CompID,
      Menu_Name: menuName,
      Menu_Publish: enableMenu,
      Menu_Orderby: menuOrderby,
      IsActiveforTill: epos,
      IsActiveforKIOSK: kiosk,
      IsActiveforTakeAway: online,
      IsActiveforDelivery: delivery,
      IsAvailableInTimeIntervals: !regularHours,
      UpdateDateTime: new Date().toISOString(),
      MenuWeeklyTimings: formattedSchedules,
      MenuGroups: menuGroups.map((group) => ({
        MenuGroupID: group.menuGroupID,
        Menu_ID: group.Menu_ID,
        LocalizeID: group.localizeID,
        CompID: group.compID,
        MenuGroup_Name: group.menuGroup_Name,
        MenuGroup_Publish: group.menuGroup_Publish,
        MenuGroup_Orderby: group.menuGroup_Orderby,
        UpdateDateTime: group.updateDateTime,
        products: group.selectedProduct.map((product) => ({
          ...product,
          Allergens: "allergens",
        })),
      })),
    };

    try {
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_MENU_AND_MENU_GROUP,
        payload
      );
      if (response.data.status) {
        showSuccessToast("Menu updated successfully");
        navigate("/Food-menu/manageFoodMenu");
      } else {
        showErrorToast(response.data.message || "Failed to update menu");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error updating menu");
      // Error silently
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = () => {
    setOriginalMenuName(menuName);
    setIsEditingName(true);
  };

  const handleSaveMenuName = async () => {
    if (!menuName.trim()) {
      showErrorToast("Menu name cannot be empty");
      return;
    }

    setLoading(true);
    const payload = {
      Menu_ID: parseInt(menuId),
      CompID: CompID,
      Menu_Name: menuName,
      UpdateDateTime: new Date().toISOString(),
    };

    try {
      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_MENU_AND_MENU_GROUP,
        payload
      );
      if (response.data.status) {
        showSuccessToast("Menu name updated successfully");
        setIsEditingName(false);
      } else {
        showErrorToast(response.data.message || "Failed to update menu name");
        setMenuName(originalMenuName);
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Error updating menu name");
      setMenuName(originalMenuName);
      // Error silently
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setMenuName(originalMenuName);
    setIsEditingName(false);
  };

  // Theme-aware button styles
  const themeButtonStyle = {
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: 'none',
    color: 'white',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: `0 2px 8px ${themeColor}40`,
    cursor: 'pointer',
  };

  const themeButtonHover = (e) => {
    e.target.style.transform = 'translateY(-1px)';
    e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
  };

  const themeButtonLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
  };

  // Theme-aware card style with subtle shadow
  const themeCardStyle = {
    backgroundColor: themeBg,
    border: `1px solid ${themeBorder}`,
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: `0 4px 20px ${themeColor}20`,
  };

  // Section card style
  const sectionCardStyle = {
    backgroundColor: isDarkMode ? '#3c4043' : '#ffffff',
    border: `1px solid ${themeBorder}`,
    borderRadius: '20px',
    padding: '32px',
    marginBottom: '32px',
    boxShadow: `0 8px 32px ${themeColor}08`,
    position: 'relative',
    overflow: 'visible',
    transition: 'all 0.3s ease',
    zIndex: 1,
  };

  // Header style for sections
  const sectionHeaderStyle = {
    color: themeText,
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    position: 'relative',
    paddingBottom: '16px',
  };

  // Pencil edit button style
  const pencilButtonStyle = {
    background: 'none',
    border: 'none',
    color: themeText + '80',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    transition: 'all 0.2s',
    fontSize: '14px',
    marginLeft: '8px',
  }

  return (
    <div className="page-wrapper" style={{
      width: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: isDarkMode ? '#1a1d23' : '#f8fafc',
      minHeight: '100vh'
    }}>
      {loading && !initialLoading && <Preloader />}

      {/* Modern Header Section */}
      <div className="content-wrapper" style={{
        background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)`,
        padding: '32px 24px 24px',
        borderBottom: `1px solid ${themeBorder}`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${themeColor}20 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }}></div>

        <div className="d-flex align-items-center justify-content-between" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            <nav aria-label="breadcrumb" style={{ background: 'none', padding: 0, marginBottom: '16px' }}>
              <ol className="breadcrumb mb-0" style={{
                fontSize: '14px',
                color: themeText,
                background: 'rgba(255,255,255,0.1)',
                padding: '8px 16px',
                borderRadius: '20px',
                display: 'inline-flex',
                backdropFilter: 'blur(10px)'
              }}>
                <li className="breadcrumb-item">
                  <a href="#/" style={{ color: themeColor, textDecoration: 'none', fontWeight: '500' }}>
                    <i className="fas fa-home me-1"></i>Home
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/Food-menu/manageFoodMenu" style={{ color: themeText + 'CC', textDecoration: 'none' }}>Manage Menu</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page" style={{ color: themeColor }}>Update Menu</li>
              </ol>
            </nav>
            <h1 className="page-header mb-2" style={{
              color: themeText,
              fontSize: '36px',
              fontWeight: '800',
              marginBottom: '12px',
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              <i className="fas fa-utensils me-3" style={{ color: themeColor, fontSize: '32px' }}></i>
              Update Menu
            </h1>
          </div>

          <button
            onClick={handleUpdate}
            className="btn px-5 d-flex align-items-center"
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '16px',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '160px',
              boxShadow: `0 8px 25px ${themeColor}40`,
              transform: 'translateY(0)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = `0 12px 35px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 8px 25px ${themeColor}40`;
            }}
          >
            <i className="fas fa-check me-2"></i>
            Update Menu
          </button>
        </div>
      </div>

      <div className="container-fluid" style={{ padding: '32px 24px' }}>
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-lg" style={{
              background: isDarkMode ? '#2d3436' : '#ffffff',
              borderRadius: '24px',
              overflow: 'visible',
              boxShadow: `0 20px 60px ${themeColor}15`,
              border: `1px solid ${themeBorder}`,
            }}>
              {/* Card Body */}
              <div style={{ padding: '40px' }}>
                {initialLoading ? (
                  <UpdateShimmer />
                ) : (
                  <div className="row mb-4 d-flex justify-content-center">
                    <div className="col-md-12">
                      {/* Menu Name and Settings Row */}
                      <div className="row g-4">
                        {/* Menu Name - 6 columns */}
                        <div className="col-6">
                          <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                              <div style={{
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                                padding: '12px',
                                borderRadius: '16px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '48px',
                                height: '48px',
                                boxShadow: `0 4px 16px ${themeColor}40`
                              }}>
                                <i className="fas fa-tag" style={{ fontSize: '18px' }}></i>
                              </div>
                              <div>
                                <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Name</div>
                                <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Update your menu name</div>
                              </div>
                            </div>
                            {isEditingName ? (
                              <div className="d-flex flex-column gap-3">
                                <FormInput
                                  type="text"
                                  value={menuName}
                                  onchange={(e) => setMenuName(e.target.value)}
                                  onBlur={handleSaveMenuName}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSaveMenuName();
                                    }
                                  }}
                                  autoFocus
                                  style={{
                                    borderRadius: '10px',
                                    border: `1px solid ${themeColor}`,
                                    backgroundColor: isDarkMode ? '#495057' : '#fff',
                                    color: themeText,
                                    padding: '14px 18px',
                                    fontSize: '16px',
                                    boxShadow: `0 2px 4px ${themeColor}10`,
                                  }}
                                />
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={handleSaveMenuName}
                                    className="btn"
                                    style={{
                                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                                      border: 'none',
                                      color: 'white',
                                      padding: '10px 20px',
                                      fontSize: '14px',
                                      borderRadius: '8px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancel}
                                    className="btn"
                                    style={{
                                      background: 'transparent',
                                      border: `1px solid ${themeText + '40'}`,
                                      color: themeText,
                                      padding: '10px 20px',
                                      fontSize: '14px',
                                      borderRadius: '8px',
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 20px',
                                background: isDarkMode ? '#495057' : '#f8f9fa',
                                borderRadius: '12px',
                                border: `1px solid ${themeBorder}`
                              }}>
                                <div style={{
                                  fontSize: '18px',
                                  fontWeight: '600',
                                  color: themeText,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px'
                                }}>
                                  <i className="fas fa-check-circle" style={{ color: themeColor, fontSize: '20px' }}></i>
                                  {menuName}
                                </div>
                                <button
                                  onClick={handleEditStart}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: themeText + '80',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.color = themeColor;
                                    e.target.style.backgroundColor = themeColor + '20';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.color = themeText + '80';
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <i className="fas fa-pencil-alt"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Menu Settings - 6 columns */}
                        <div className="col-6">
                          <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                              <div style={{
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                                padding: '12px',
                                borderRadius: '16px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '48px',
                                height: '48px',
                                boxShadow: `0 4px 16px ${themeColor}40`
                              }}>
                                <i className="fas fa-cog" style={{ fontSize: '18px' }}></i>
                              </div>
                              <div>
                                <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Settings</div>
                                <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Configure availability and channels</div>
                              </div>
                            </div>
                            <div className="row g-3">
                              {/* Row 1: Status & Online Orders */}
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={enableMenu}
                                  onToggle={() => setEnableMenu(!enableMenu)}
                                  label={"Status"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={online}
                                  onToggle={() => setOnline(!online)}
                                  label={"Online Orders"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              {/* Row 2: Delivery & E-POS */}
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={delivery}
                                  onToggle={() => setDelivery(!delivery)}
                                  label={"Delivery"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={epos}
                                  onToggle={() => setEpos(!epos)}
                                  label={"E-POS"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              <div className="col-6" style={{ display: 'none' }}>
                                <ToggleSwitch
                                  isChecked={kiosk}
                                  onToggle={() => setKiosk(!kiosk)}
                                  label={"KIOSK"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>


                      {/* Menu Groups Section */}
                      <div style={sectionCardStyle}>
                        <div style={sectionHeaderStyle}>
                          <div style={{
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                            padding: '12px',
                            borderRadius: '16px',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '48px',
                            height: '48px',
                            boxShadow: `0 4px 16px ${themeColor}40`
                          }}>
                            <i className="fas fa-list-ul" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Groups</div>
                            <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Organize your menu items into categories. Drag to reorder.</div>
                          </div>
                        </div>
                        <p style={{ color: themeText + '80', fontSize: '14px', marginBottom: '20px' }}>
                          Organize your menu items into categories. Drag to reorder.
                        </p>
                        {menuGroups.map((group, index) => (
                          <div
                            key={index}
                            className="row mb-3 align-items-center"
                            style={{
                              backgroundColor: isDarkMode ? '#495057' : '#f8f9fa',
                              borderRadius: '12px',
                              padding: '20px',
                              border: `1px solid ${themeBorder}`,
                              cursor: 'grab',
                              transition: 'all 0.3s ease',
                              opacity: draggedIndex === index ? 0.8 : 1,
                              boxShadow: `0 2px 8px ${themeColor}10`,
                              transform: draggedIndex === index ? 'scale(0.98)' : 'scale(1)',
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="col-12">
                              <div className="d-flex align-items-center">
                                <div className="d-flex align-items-center flex-grow-1">
                                  <i
                                    className={`fas fa-chevron-${group.expanded ? "down" : "right"
                                      } me-2`}
                                    style={{ cursor: "pointer", color: themeText }}
                                    onClick={() => toggleGroupExpansion(index)}
                                  ></i>
                                  <i
                                    className="fas fa-grip-vertical text-muted me-2"
                                    style={{ fontSize: '20px', cursor: 'grab', color: themeText + '60' }}
                                  />
                                  <div className="flex-grow-1">
                                    <FormInput
                                      placeHolder={`Group ${index + 1} (e.g., Appetizers, Main Course)`}
                                      type={"text"}
                                      value={group.menuGroup_Name}
                                      onchange={(e) =>
                                        handleMenuGroupChange(index, e.target.value)
                                      }
                                      style={{
                                        borderRadius: '10px',
                                        border: `1px solid ${themeBorder}`,
                                        backgroundColor: isDarkMode ? '#495057' : '#fff',
                                        color: themeText,
                                        padding: '12px 16px',
                                        fontSize: '16px',
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="d-flex align-items-center ms-2">
                                  {menuGroups.length > 1 && (
                                    <i
                                      className="fas fa-trash text-danger"
                                      style={{
                                        fontSize: '18px',
                                        cursor: 'pointer',
                                        padding: '8px',
                                        borderRadius: '50%',
                                        transition: 'all 0.2s',
                                      }}
                                      onClick={() => handleDeleteMenuGroup(index)}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#dc354520';
                                        e.target.style.transform = 'scale(1.1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.transform = 'scale(1)';
                                      }}
                                    ></i>
                                  )}
                                </div>
                              </div>
                              {group.expanded && (
                                <div className="mt-2 p-3" style={{
                                  backgroundColor: isDarkMode ? '#495057' : '#f8f9fa',
                                  borderRadius: '8px',
                                  border: `1px solid ${themeBorder}`
                                }}>
                                  {group.selectedProduct &&
                                    group.selectedProduct.length > 0 ? (
                                    <>
                                      <div className="mb-3">
                                        <h6 style={{ color: themeText, marginBottom: '12px' }}>Selected Products:</h6>
                                        {group.selectedProduct.map(
                                          (product, prodIndex) => (
                                            <div
                                              key={prodIndex}
                                              className="d-flex justify-content-between align-items-center mb-2 p-2"
                                              style={{
                                                backgroundColor: themeBg,
                                                borderRadius: '6px',
                                                border: `1px solid ${themeBorder}`
                                              }}
                                            >
                                              <div className="d-flex align-items-center">
                                                {product.menuItem_Image ? (
                                                  <img
                                                    src={product.menuItem_Image}
                                                    alt={product.menuItem_Name}
                                                    style={{
                                                      width: "30px",
                                                      height: "30px",
                                                      objectFit: "contain",
                                                      marginRight: "10px",
                                                      borderRadius: '4px',
                                                    }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      width: "30px",
                                                      height: "30px",
                                                      backgroundColor: themeBorder,
                                                      marginRight: "10px",
                                                      borderRadius: '4px',
                                                    }}
                                                  />
                                                )}
                                                <span style={{ color: themeText }}>{product.menuItem_Name}</span>
                                              </div>
                                              <i
                                                className="fas fa-trash text-danger"
                                                style={{ cursor: "pointer", fontSize: '16px' }}
                                                onClick={() => {
                                                  setMenuGroups((prev) =>
                                                    prev.map((g, i) =>
                                                      i === index
                                                        ? {
                                                            ...g,
                                                            selectedProduct:
                                                              g.selectedProduct.filter(
                                                                (_, pIdx) =>
                                                                  pIdx !== prodIndex
                                                              ),
                                                          }
                                                        : g
                                                    )
                                                  );
                                                }}
                                              ></i>
                                            </div>
                                          )
                                        )}
                                      </div>
                                      <div className="text-center">
                                        <button
                                          className="btn btn-sm"
                                          onClick={() => handleAddProduct(index)}
                                          style={{
                                            ...themeButtonStyle,
                                            borderRadius: '20px',
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                          }}
                                          onMouseEnter={themeButtonHover}
                                          onMouseLeave={themeButtonLeave}
                                        >
                                          <i className="fas fa-plus me-1"></i>
                                          Add More Items
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-center" style={{ padding: '20px' }}>
                                      <p style={{ color: themeText + '80', marginBottom: '12px' }}>This menu group is empty</p>
                                      <button
                                        className="btn btn-sm"
                                        onClick={() => handleAddProduct(index)}
                                        style={{
                                          ...themeButtonStyle,
                                          borderRadius: '20px',
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                        }}
                                        onMouseEnter={themeButtonHover}
                                        onMouseLeave={themeButtonLeave}
                                      >
                                        <i className="fas fa-plus me-1"></i>
                                        Add Item
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className="text-center mt-4">
                          <button
                            className="btn"
                            onClick={handleAddMenuGroup}
                            style={{
                              background: 'transparent',
                              border: `2px dashed ${themeColor}`,
                              color: themeColor,
                              padding: '12px 24px',
                              fontSize: '15px',
                              borderRadius: '12px',
                              fontWeight: '600',
                              transition: 'all 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = `${themeColor}15`;
                              e.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.transform = 'scale(1)';
                            }}
                          >
                            <i className="fas fa-plus me-2"></i>
                            Add Group
                          </button>
                        </div>
                      </div>

                      {/* Time Slot Selection Section */}
                      <div style={sectionCardStyle}>
                        <div style={sectionHeaderStyle}>
                          <div style={{
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                            padding: '12px',
                            borderRadius: '16px',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '48px',
                            height: '48px',
                            boxShadow: `0 4px 16px ${themeColor}40`
                          }}>
                            <i className="fas fa-clock" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Time Slot Configuration</div>
                            <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Set custom availability hours</div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <ToggleSwitch
                            isChecked={regularHours}
                            onToggle={handleRegularHoursToggle}
                            label={"Available during regular business hours"}
                            disabled={false}
                            labelStyle={toggleLabelStyle}
                            themeColor={themeColor}
                          />
                        </div>
                        {!regularHours && (
                          <div style={{ 
                            padding: '20px 16px', 
                            backgroundColor: themeBg, 
                            borderRadius: '12px', 
                            marginTop: '16px',
                            position: 'relative',
                            zIndex: 10,
                            overflow: 'visible'
                          }}>
                            <ToggleSwitch
                              isChecked={useSameTimeSlots}
                              onToggle={handleSameTimeSlotsToggle}
                              label={"Apply same time slots to all days"}
                              disabled={false}
                              labelStyle={toggleLabelStyle}
                              themeColor={themeColor}
                            />
                            {useSameTimeSlots ? (
                              <CommonTimeSlotForAllWeek
                                setCommonTimeSlots={(slots) => {
                                  // Ensure slots is always treated as an array
                                  const slotArray = Array.isArray(slots)
                                    ? slots
                                    : [];
                                  // Update the commonTimeSlot state
                                  setCommonTimeSlot(slotArray);
                                  // Update savedData
                                  setSavedData((prev) => ({
                                    ...prev,
                                    commonTimeSlot: slotArray,
                                  }));
                                  // Apply to all schedules
                                  const updatedSchedules = schedules.map(
                                    (schedule) => ({
                                      ...schedule,
                                      timeSlots: slotArray.map((slot) => ({
                                        ...slot,
                                      })),
                                    })
                                  );
                                  setSchedules(updatedSchedules);
                                }}
                                commonTimeSlots={commonTimeSlot}
                                isProfilePage={false}
                              />
                            ) : (
                              <ScheduleManager
                                schedules={schedules}
                                setSchedules={(updatedSchedules) => {
                                  setSchedules(updatedSchedules);
                                  setSavedData((prev) => ({
                                    ...prev,
                                    schedules: updatedSchedules,
                                  }));
                                }}
                                isProfilePage={false}
                                isDarkMode={isDarkMode}
                                themeColor={themeColor}
                              />
                            )}
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ProductMultiSelectionModal
        showModal={showModal}
        setShowModal={setShowModal}
        showButton={true}
        onSubmit={handleProductSelectionSubmit}
        setSelectedProducts={setSelectedProducts}
        selectedProducts={selectedProducts}
        products={products}
      />
    </div>
  );
}

export default UpdateMenu;